import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CommonService } from './common.service';

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsIdentifiersService implements OnDestroy {
  private _commonService = inject(CommonService);
  private _router = inject(Router);

  private readonly _targetSelector =
    'button, a[role="button"], [role="button"], [role="tab"], .mat-mdc-tab, .mat-tab-link, [mat-tab-link], .mdc-tab';

  private _observer?: MutationObserver;
  private _navigationSubscription?: Subscription;
  private _initialized = false;
  private _pendingScan = false;
  private _queuedRoots = new Set<HTMLElement>();
  private _idSequence = new Map<string, number>();

  ngOnDestroy(): void {
    this._observer?.disconnect();
    this._navigationSubscription?.unsubscribe();
  }

  public init(): void {
    if (this._initialized || !this._commonService.isPlatformBrowser) {
      return;
    }

    this._initialized = true;
    this._scanAndAssignIdentifiers(document.body);
    setTimeout(() => this._scanAndAssignIdentifiers(document.body), 150);

    this._navigationSubscription = this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this._queuedRoots.add(document.body);
        this._scheduleScan();
      }
    });

    this._observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of Array.from(record.addedNodes)) {
          if (node instanceof HTMLElement) {
            this._queuedRoots.add(node);
          }
        }
      }

      this._scheduleScan();
    });

    this._observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private _scheduleScan(): void {
    if (this._pendingScan) {
      return;
    }

    this._pendingScan = true;
    const runScan = () => {
      this._pendingScan = false;

      if (!this._queuedRoots.size) {
        return;
      }

      const roots = Array.from(this._queuedRoots);
      this._queuedRoots.clear();

      for (const root of roots) {
        this._scanAndAssignIdentifiers(root);
      }
    };

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(runScan);
      return;
    }

    setTimeout(runScan, 0);
  }

  private _scanAndAssignIdentifiers(root: ParentNode): void {
    const currentRoute = this._getRouteSlug();
    const elements = this._collectTrackableElements(root);

    for (const element of elements) {
      const existingGaId = element.getAttribute('data-ga-id');
      if (existingGaId) {
        if (!element.id) {
          element.id = existingGaId;
        }
        continue;
      }

      const type = this._isTabElement(element) ? 'tab' : 'button';
      const label = this._getElementLabel(element);
      const baseKey = `${currentRoute}-${type}-${label}`;
      const count = this._nextCount(baseKey);

      const gaId = `ga-${baseKey}-${count}`;
      element.setAttribute('data-ga-id', gaId);
      element.setAttribute('data-ga-type', type);

      if (!element.id) {
        element.id = gaId;
      }
    }
  }

  private _collectTrackableElements(root: ParentNode): HTMLElement[] {
    const elements = Array.from(
      root.querySelectorAll<HTMLElement>(this._targetSelector)
    );

    if (root instanceof HTMLElement && root.matches(this._targetSelector)) {
      elements.unshift(root);
    }

    return elements;
  }

  private _nextCount(baseKey: string): number {
    const count = (this._idSequence.get(baseKey) ?? 0) + 1;
    this._idSequence.set(baseKey, count);
    return count;
  }

  private _isTabElement(element: HTMLElement): boolean {
    if (element.getAttribute('role') === 'tab') {
      return true;
    }

    const className = element.className?.toString().toLowerCase() ?? '';
    return className.includes('tab');
  }

  private _getRouteSlug(): string {
    const routePath = this._router.url.split('?')[0].replace(/^\//, '');
    return this._slugify(routePath || 'home');
  }

  private _getElementLabel(element: HTMLElement): string {
    const candidates = [
      element.getAttribute('aria-label'),
      element.getAttribute('title'),
      element.getAttribute('name'),
      element.getAttribute('id'),
      element.textContent,
    ];

    for (const candidate of candidates) {
      const normalized = this._slugify(candidate ?? '');
      if (normalized) {
        return normalized;
      }
    }

    return 'item';
  }

  private _slugify(value: string): string {
    return value
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-_]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }
}
